import { useEffect, type RefObject } from "react"

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Keeps keyboard focus inside `ref` while `active` (accessibility.md §8):
 * focuses the `[data-autofocus]` element, or the first focusable one, on the
 * next frame; cycles Tab between the first and the last focusable element;
 * and when it deactivates returns focus to whatever held it before.
 *
 * Extracted from `Modal` so the preview drawer traps the same way. Escape and
 * the scroll lock stay with each caller: the drawer's Escape has to tell its
 * own keystrokes from a dialog stacked on top of it.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return
    const previousFocus = document.activeElement as HTMLElement | null
    // Defer focus to next paint so animations don't fight us.
    const handle = window.requestAnimationFrame(() => {
      const target =
        ref.current?.querySelector<HTMLElement>("[data-autofocus]") ??
        ref.current?.querySelector<HTMLElement>(FOCUSABLE)
      target?.focus()
    })
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !ref.current) return
      const focusables = ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const current = document.activeElement as HTMLElement | null
      if (e.shiftKey && current === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && current === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => {
      window.cancelAnimationFrame(handle)
      document.removeEventListener("keydown", onKey)
      // Only if the trigger still exists: an action inside the trap can
      // remove it (accepting a level removes its "Revisar" pill), and
      // focusing a detached node silently drops focus on <body>. The caller
      // then picks the next logical control.
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [active, ref])
}
